import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import {
  BookOpen,
  CalendarCheck,
  ChevronRight,
  ClipboardList,
  GraduationCap,
  LineChart,
  RotateCcw,
  Target,
  Layers,
  Check,
  X,
} from "lucide-react";
import { seo, breadcrumbScript, SITE_URL, OG_IMAGE, SITE_NAME } from "@/lib/seo";
import { FaqAccordion, type FaqItem } from "@/components/marketing/faq-accordion";
import { JeeTrackerVisual } from "@/components/marketing/jee-tracker-visual";
import { StartFreeButton } from "@/components/marketing/marketing-kit";

const TITLE = "Free JEE Preparation Tracker & Study Planner | The Quantum Minds";
const DESC =
  "Track your JEE syllabus, lectures, revision, questions, tests and backlogs with a free JEE preparation tracker from The Quantum Minds.";

const FAQS: FaqItem[] = [
  {
    q: "What is a JEE preparation tracker?",
    a: "A JEE preparation tracker is a tool that records what you have actually studied — chapters, lectures, revisions, questions and tests — and turns that record into a daily plan and a completion percentage you can trust.",
  },
  {
    q: "Is this JEE preparation tracker free?",
    a: "Yes. Syllabus and lecture tracking, the revision planner, question practice and the test tracker are free to use. A few advanced tools are part of an optional premium plan.",
  },
  {
    q: "How does a JEE preparation tracker help students?",
    a: "It removes guesswork. Instead of remembering what was studied and what was skipped, you open one list that shows the revisions due today, the questions still pending and the chapters you have not finished.",
  },
  {
    q: "Can I track my JEE syllabus?",
    a: "Yes. Add Physics, Chemistry and Maths chapters with their lecture counts, and the app calculates chapter-wise and subject-wise completion from the lectures you tick off.",
  },
  {
    q: "Can I track JEE lectures?",
    a: "Yes. Each chapter holds its individual lectures. Mark one complete the day you finish it and the dashboard shows which lecture comes next.",
  },
  {
    q: "Can I plan JEE revision?",
    a: "Yes. Completing a lecture schedules spaced revisions for it automatically. Each day the revision planner shows what is due, and missed revisions carry forward instead of disappearing.",
  },
  {
    q: "Can I track question practice?",
    a: "Yes. Set a question target for a chapter and log the questions you actually solved, so you can tell the chapters you practised from the ones you only watched.",
  },
  {
    q: "Can I track JEE tests?",
    a: "Yes. Create a test with the chapters it covers, get pre-test revision tasks, then record subject-wise marks afterwards and see averages, best and worst scores and your trend.",
  },
  {
    q: "Can I manage JEE backlogs?",
    a: "Yes. Anything you miss — a revision, a practice target — stays visible as pending work you can clear later rather than being silently dropped.",
  },
  {
    q: "Is the JEE tracker useful for droppers?",
    a: "Yes. Droppers usually carry a large syllabus and a long revision cycle, which is exactly the situation where a tracked backlog and an automatic revision schedule help most.",
  },
  {
    q: "Can I use the tracker while studying through coaching?",
    a: "Yes. The tracker is source-agnostic. Add the chapters and lecture counts from your coaching module, school or recorded course and track them the same way.",
  },
  {
    q: "Can I use the JEE preparation tracker on mobile?",
    a: "Yes. It works in any modern mobile browser, syncs across devices once you sign in, and can be installed to your home screen on Android and iOS.",
  },
];

export const Route = createFileRoute("/jee-preparation-tracker")({
  head: () => ({
    ...seo({ title: TITLE, description: DESC, path: "/jee-preparation-tracker" }),
    scripts: [
      breadcrumbScript([
        { name: "Home", path: "/" },
        { name: "JEE Preparation Tracker", path: "/jee-preparation-tracker" },
      ]),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: TITLE,
          description: DESC,
          url: `${SITE_URL}/jee-preparation-tracker`,
          inLanguage: "en-IN",
          isPartOf: {
            "@type": "WebSite",
            name: SITE_NAME,
            url: SITE_URL,
          },
          primaryImageOfPage: OG_IMAGE,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: SITE_NAME,
          url: `${SITE_URL}/jee-preparation-tracker`,
          applicationCategory: "EducationalApplication",
          operatingSystem: "Web, Android, iOS",
          browserRequirements: "Requires a modern web browser with JavaScript enabled",
          inLanguage: "en",
          image: OG_IMAGE,
          description: DESC,
          featureList: [
            "JEE syllabus and chapter tracking",
            "Lecture completion tracking",
            "Spaced-repetition revision planner",
            "Question practice targets",
            "Test planning and score tracking",
            "Backlog carry-forward",
            "Daily progress and streaks",
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: JeeTrackerPage,
});

/* ---------------------------------------------------------------- content */

const PROBLEMS = [
  {
    problem: "“I studied this chapter weeks ago, but I don't know if I ever revised it.”",
    solution: "Every lecture you complete enters the revision schedule, so revision is tracked, not remembered.",
  },
  {
    problem: "“I lost track of which lecture I was on in this chapter.”",
    solution: "Lectures are ticked off one by one, and the next pending lecture is always shown.",
  },
  {
    problem: "“Revision keeps getting pushed to tomorrow.”",
    solution: "Due revisions appear in one daily list, and skipped ones stay visible as pending work.",
  },
  {
    problem: "“Some chapters I've practised a lot, others barely at all.”",
    solution: "Chapter-wise question targets show solved versus target instead of a vague feeling.",
  },
  {
    problem: "“Backlogs build up quietly and I notice them too late.”",
    solution: "Missed items carry forward and stay counted until you clear them.",
  },
  {
    problem: "“Test preparation always turns into a last-night rush.”",
    solution: "Add a test with its chapters and the revision for those chapters spreads across the days left.",
  },
  {
    problem: "“I don't actually know how much of the syllabus is done.”",
    solution: "Completion is calculated from lectures finished, so the percentage reflects real work.",
  },
  {
    problem: "“My timetable looks good but I never measure whether I followed it.”",
    solution: "The tracker records execution — what was completed, on which day — not just the plan.",
  },
];

const STEPS = [
  { n: "01", title: "Add your chapters", body: "Enter the Physics, Chemistry and Maths chapters you have to cover, with their lecture counts." },
  { n: "02", title: "Track lectures", body: "Each chapter holds its own lectures, so progress is measured in real units of work." },
  { n: "03", title: "Complete lectures", body: "Tick a lecture the day you finish it, from coaching, school or a recorded course." },
  { n: "04", title: "Revision gets scheduled", body: "Spaced revisions for that lecture are created automatically from the completion date." },
  { n: "05", title: "Track questions", body: "Set a question target per chapter and log what you actually solved." },
  { n: "06", title: "Plan tests", body: "Add an upcoming test with its chapters and get revision tasks before the test date." },
  { n: "07", title: "Clear backlogs", body: "Anything missed carries forward as pending work you can clear on a lighter day." },
  { n: "08", title: "Monitor progress", body: "Progress rings, a study calendar and streaks show how consistent you have been." },
];

const FEATURES = [
  { icon: BookOpen, title: "Syllabus Tracking", body: "Add every Physics, Chemistry and Maths chapter you have to cover. Completion is computed from lectures finished, so your JEE syllabus tracker shows real progress, not an estimate." },
  { icon: GraduationCap, title: "Lecture Tracking", body: "Tick lectures off as you finish them. The dashboard always tells you which lecture is next, so you never lose the thread mid-chapter." },
  { icon: CalendarCheck, title: "Revision Planning", body: "Each completed lecture is scheduled for spaced revision. Open the planner and the day's revision list is already built for you." },
  { icon: ClipboardList, title: "Question Practice", body: "Set a target per chapter and log solved questions, so practice stays honest across the whole syllabus." },
  { icon: Target, title: "Test Tracking", body: "Create a test plan around the chapters a test covers, then record subject-wise marks and watch the trend." },
  { icon: RotateCcw, title: "Backlog Tracking", body: "Missed revisions and pending practice are carried forward and stay visible until cleared." },
  { icon: LineChart, title: "Overall Progress", body: "Daily progress rings, a study calendar and streaks turn consistency into something you can actually see." },
];

const COMPARE: { row: string; timetable: string; tracker: string }[] = [
  { row: "What to study", timetable: "Planned in advance", tracker: "Planned and recorded" },
  { row: "Execution", timetable: "Rarely measured", tracker: "Tracked lecture by lecture" },
  { row: "Revision", timetable: "Easy to forget", tracker: "Scheduled automatically" },
  { row: "Backlogs", timetable: "Slip out of view", tracker: "Stay visible until cleared" },
  { row: "Questions & tests", timetable: "Tracked separately, if at all", tracker: "Logged in the same place" },
  { row: "Progress", timetable: "Estimated", tracker: "Calculated from your own data" },
];

const AUDIENCE = [
  "Aspirants just starting JEE preparation",
  "Class 11 students building the base",
  "Class 12 students balancing boards and JEE",
  "Droppers managing a full-syllabus revision cycle",
  "Students in offline or online coaching",
  "Students studying through recorded lectures",
  "Anyone stuck with a growing backlog",
  "Anyone who wants structured revision",
];

const TRACKABLE = [
  "JEE Syllabus",
  "Lectures",
  "Revision",
  "Questions",
  "Tests",
  "Backlogs",
  "Daily Study",
  "Overall Progress",
];

const TOOLS: { to: string; label: string; note: string }[] = [
  { to: "/revision-tracker", label: "JEE revision planner", note: "Spaced repetition built from your lectures" },
  { to: "/test-tracker", label: "JEE test tracker", note: "Plan revision before a test, log marks after" },
  { to: "/study-planner", label: "JEE study planner", note: "Turn your syllabus into a daily plan" },
  { to: "/pyq-predictor", label: "JEE PYQ predictor", note: "Chapter-wise previous-year question trends" },
  { to: "/college-predictor", label: "JEE college predictor", note: "Explore colleges against a rank range" },
  { to: "/prerequisites", label: "Chapter prerequisites", note: "See what a chapter depends on first" },
  { to: "/simulations", label: "Physics & chemistry simulations", note: "Visualise concepts interactively" },
  { to: "/mindmaps", label: "Chapter mindmaps", note: "Structured maps for PCM and Biology" },
];

/* ------------------------------------------------------------- primitives */

function Reveal({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function SectionHeading({ eyebrow, children }: { eyebrow?: string; children: React.ReactNode }) {
  return (
    <>
      {eyebrow ? (
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {eyebrow}
        </p>
      ) : null}
      <h2 className="mt-1.5 text-[22px] font-extrabold tracking-tight sm:text-[26px]">{children}</h2>
    </>
  );
}

/* ------------------------------------------------------------------- page */

function JeeTrackerPage() {
  return (
    <div className="relative">
      <div aria-hidden className="aurora pointer-events-none fixed inset-0 -z-10" />
      <main className="mx-auto max-w-2xl px-5 pb-20 pt-6">
        <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
          <ol className="flex items-center gap-1.5">
            <li>
              <Link to="/" className="hover:text-foreground">
                Home
              </Link>
            </li>
            <li aria-hidden>
              <ChevronRight className="size-3" />
            </li>
            <li aria-current="page" className="font-semibold text-foreground">
              JEE Preparation Tracker
            </li>
          </ol>
        </nav>

        {/* Hero */}
        <section className="mt-7">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            JEE preparation tracker
          </p>
          <h1 className="mt-2 text-[32px] font-extrabold leading-[1.1] tracking-tight sm:text-[42px]">
            Free JEE Preparation Tracker
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground">
            Track your JEE syllabus, lectures, revision, questions, tests and backlogs in one place.
            The Quantum Minds turns your study activity into a clear daily plan and gives you an
            honest picture of your preparation.
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <StartFreeButton label="Start Tracking Free" />
            <Link
              to="/revisions"
              className="glass inline-flex h-13 items-center justify-center rounded-full border border-hairline px-6 text-[15px] font-semibold transition-transform active:scale-95"
            >
              Explore the Revision Planner
            </Link>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Free to start. No card required. Sign in only when you want to save progress.
          </p>

          <div className="mt-8">
            <JeeTrackerVisual />
          </div>
        </section>

        {/* What is */}
        <Reveal className="mt-14">
          <SectionHeading>What Is a JEE Preparation Tracker?</SectionHeading>
          <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground">
            <p>
              A JEE preparation tracker is a system that records what you have actually studied and
              turns that record into your next steps. A normal timetable only describes intentions:
              it says two hours of Physics on Tuesday, but nothing in it knows whether those two
              hours happened, which lecture they covered, or when that chapter should come back for
              revision.
            </p>
            <p>
              That gap is where most preparation quietly breaks down. Lectures get watched and never
              revised. A chapter feels finished because it was studied once. Question practice
              concentrates on the chapters you already like. Backlogs grow without ever being
              counted.
            </p>
            <p>
              A tracker closes the gap by keeping one shared record of syllabus, lectures, revision,
              question practice, tests and backlogs. Because everything lives together, completing a
              lecture can schedule its revision, adding a test can create the revision it needs, and
              your completion percentage is calculated from real work rather than a guess.
            </p>
          </div>
        </Reveal>

        {/* Why */}
        <Reveal className="mt-14">
          <SectionHeading eyebrow="Problem → solution">
            Why Use a JEE Preparation Tracker?
          </SectionHeading>
          <div className="mt-4 space-y-3">
            {PROBLEMS.map((p) => (
              <div key={p.problem} className="glass rounded-[22px] border border-hairline p-4">
                <p className="text-sm font-semibold leading-relaxed">{p.problem}</p>
                <p className="mt-2 flex items-start gap-2 text-sm leading-relaxed text-muted-foreground">
                  <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-surface">
                    <Check aria-hidden className="size-3 text-primary" />
                  </span>
                  <span>{p.solution}</span>
                </p>
              </div>
            ))}
          </div>
        </Reveal>

        {/* How it works */}
        <Reveal className="mt-14">
          <SectionHeading>How the JEE Preparation Tracker Works</SectionHeading>
          <ol className="mt-5 space-y-0">
            {STEPS.map((s, i) => (
              <motion.li
                key={s.n}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: Math.min(i, 3) * 0.05 }}
                className="relative flex gap-4 pb-6 last:pb-0"
              >
                <div className="flex flex-col items-center">
                  <span className="neural-gradient grid size-9 shrink-0 place-items-center rounded-xl text-[11px] font-extrabold text-white">
                    {s.n}
                  </span>
                  {i < STEPS.length - 1 ? (
                    <span aria-hidden className="mt-1 w-px flex-1 bg-hairline opacity-60" />
                  ) : null}
                </div>
                <div className="pt-1">
                  <h3 className="text-[15px] font-bold tracking-tight">{s.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
                </div>
              </motion.li>
            ))}
          </ol>
        </Reveal>

        {/* Features */}
        <Reveal className="mt-14">
          <SectionHeading>Everything You Need to Track Your JEE Preparation</SectionHeading>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {FEATURES.map((f) => (
              <motion.div
                key={f.title}
                whileHover={{ y: -3 }}
                transition={{ type: "spring", stiffness: 300, damping: 24 }}
                className="glass rounded-[24px] border border-hairline p-5"
              >
                <div className="flex items-center gap-3">
                  <span className="neural-gradient grid size-10 shrink-0 place-items-center rounded-2xl">
                    <f.icon aria-hidden className="size-4 text-white" />
                  </span>
                  <h3 className="text-[16px] font-bold tracking-tight">{f.title}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </Reveal>

        {/* Visualisation */}
        <Reveal className="mt-14">
          <SectionHeading>See Your JEE Preparation Clearly</SectionHeading>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Instead of guessing how your preparation is going, you can see it: subject-wise syllabus
            completion, the revisions due today, lectures pending, questions logged, backlog count
            and the test you are preparing for — on one screen. The view below uses example data.
          </p>
          <div className="mt-5">
            <JeeTrackerVisual />
          </div>
          <div className="mt-4">
            <StartFreeButton label="Track Your JEE Preparation" />
          </div>
        </Reveal>

        {/* Comparison */}
        <Reveal className="mt-14">
          <SectionHeading>JEE Preparation Tracker vs a Normal Timetable</SectionHeading>
          <div className="glass mt-4 overflow-hidden rounded-[24px] border border-hairline">
            <table className="w-full text-left text-sm">
              <caption className="sr-only">
                Comparison of a normal JEE timetable and a JEE preparation tracker
              </caption>
              <thead>
                <tr className="border-b border-hairline text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th scope="col" className="px-3 py-3 font-semibold">
                    Area
                  </th>
                  <th scope="col" className="px-3 py-3 font-semibold">
                    Timetable
                  </th>
                  <th scope="col" className="px-3 py-3 font-semibold">
                    Tracker
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARE.map((c) => (
                  <tr key={c.row} className="border-b border-hairline last:border-0 align-top">
                    <th scope="row" className="px-3 py-3 text-[13px] font-semibold">
                      {c.row}
                    </th>
                    <td className="px-3 py-3 text-[13px] text-muted-foreground">
                      <span className="flex items-start gap-1.5">
                        <X aria-hidden className="mt-0.5 size-3 shrink-0 opacity-60" />
                        {c.timetable}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-[13px]">
                      <span className="flex items-start gap-1.5">
                        <Check aria-hidden className="mt-0.5 size-3 shrink-0 text-primary" />
                        {c.tracker}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>

        {/* Why us */}
        <Reveal className="mt-14">
          <SectionHeading>Why Students Use The Quantum Minds</SectionHeading>
          <ul className="mt-4 space-y-2.5 text-sm">
            {[
              "One place for the complete preparation workflow, instead of four half-used apps",
              "Tracks the work you actually completed, not only what you planned",
              "Connects lectures directly to revision, so retention is scheduled",
              "Keeps backlogs visible rather than letting them disappear",
              "Shows progress you can act on: chapters, revisions, questions, tests",
              "Free to start, with your data synced once you sign in",
              "Built around competitive-exam preparation, not general to-do lists",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2.5">
                <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-surface">
                  <Check aria-hidden className="size-3 text-primary" />
                </span>
                <span className="text-muted-foreground">{item}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        {/* Weekly routine */}
        <Reveal className="mt-14">
          <SectionHeading>A Simple Weekly JEE Preparation Routine</SectionHeading>
          <ol className="mt-4 space-y-3">
            {[
              "Add the chapters you are currently studying, with their lecture counts.",
              "Track each lecture on the day you complete it, so revisions start from the right date.",
              "Clear the day's revision list before starting new lectures.",
              "Log your question practice against the chapter you practised.",
              "Add upcoming tests as soon as the dates are announced.",
              "Review carry-forward items on the weekend and clear your backlog.",
            ].map((step, i) => (
              <li key={step} className="flex items-start gap-3">
                <span className="grid size-7 shrink-0 place-items-center rounded-full border border-hairline bg-surface text-xs font-bold">
                  {i + 1}
                </span>
                <span className="pt-1 text-sm leading-relaxed text-muted-foreground">{step}</span>
              </li>
            ))}
          </ol>
          <div className="mt-5">
            <StartFreeButton label="Start Your JEE Preparation Tracker" />
          </div>
        </Reveal>

        {/* Audience */}
        <Reveal className="mt-14">
          <SectionHeading>Who Is This JEE Preparation Tracker For?</SectionHeading>
          <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
            {AUDIENCE.map((a) => (
              <div
                key={a}
                className="glass rounded-2xl border border-hairline px-4 py-3 text-sm text-muted-foreground"
              >
                {a}
              </div>
            ))}
          </div>
        </Reveal>

        {/* Trackable */}
        <Reveal className="mt-14">
          <SectionHeading>What You Can Track With The Quantum Minds</SectionHeading>
          <ul className="mt-4 flex flex-wrap gap-2.5">
            {TRACKABLE.map((t) => (
              <li
                key={t}
                className="glass inline-flex items-center gap-2 rounded-full border border-hairline px-4 py-2 text-xs font-semibold"
              >
                <Check aria-hidden className="size-3 text-primary" />
                {t}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            You can also{" "}
            <Link to="/questions" className="font-semibold text-foreground underline">
              log chapter-wise question practice
            </Link>{" "}
            and{" "}
            <Link to="/tests" className="font-semibold text-foreground underline">
              record test scores with subject-wise marks
            </Link>
            , or start from{" "}
            <Link to="/" className="font-semibold text-foreground underline">
              your study dashboard
            </Link>
            .
          </p>
        </Reveal>

        {/* Related tools */}
        <Reveal className="mt-14">
          <SectionHeading>More JEE Preparation Tools</SectionHeading>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {TOOLS.map((t) => (
              <Link
                key={t.to}
                to={t.to}
                className="glass flex items-start gap-3 rounded-2xl border border-hairline px-4 py-3.5 transition-transform active:scale-[0.98]"
              >
                <Layers aria-hidden className="mt-0.5 size-4 shrink-0 text-primary" />
                <span className="flex flex-col">
                  <span className="text-sm font-semibold">{t.label}</span>
                  <span className="mt-0.5 text-xs text-muted-foreground">{t.note}</span>
                </span>
              </Link>
            ))}
          </div>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            Preparing for another exam? The same tracker works for{" "}
            <Link to="/neet" className="font-semibold text-foreground underline">
              NEET preparation tracking
            </Link>{" "}
            and{" "}
            <Link to="/jee" className="font-semibold text-foreground underline">
              JEE Main and Advanced
            </Link>
            .
          </p>
        </Reveal>

        {/* FAQ */}
        <Reveal className="mt-14">
          <SectionHeading>Frequently Asked Questions</SectionHeading>
          <p className="mt-1 text-sm text-muted-foreground">About JEE preparation trackers</p>
          <div className="mt-4">
            <FaqAccordion items={FAQS} idPrefix="jee-tracker-faq" />
          </div>
        </Reveal>

        {/* Closing CTA */}
        <Reveal className="mt-14">
          <div className="glass relative overflow-hidden rounded-[28px] border border-hairline p-6 text-center">
            <div
              aria-hidden
              className="neural-gradient pointer-events-none absolute -top-16 left-1/2 size-40 -translate-x-1/2 rounded-full opacity-25 blur-3xl"
            />
            <h2 className="relative text-[20px] font-extrabold tracking-tight">
              Start Tracking Your JEE Preparation
            </h2>
            <p className="relative mt-2 text-sm leading-relaxed text-muted-foreground">
              Stop guessing how much of your preparation is actually complete. Track your syllabus,
              lectures, revision, questions, tests and backlogs in one place.
            </p>
            <div className="relative mt-5 flex justify-center">
              <StartFreeButton label="Use The Quantum Minds Free" />
            </div>
          </div>
        </Reveal>
      </main>
    </div>
  );
}
